import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import {
  PLAN_ENTITY_LEGEND,
  PlanLegend,
  TwinPlanner,
  checksToDo,
  type SidebarMode,
} from '@repo/ui/planner';
import { Tag, ToastRegion } from '@repo/design-system';
import type { UnderlayImages } from '@repo/front-end-services';
import {
  registerReadState,
  useEditorStore,
  useEquipmentCatalogStore,
  useFloorplanStore,
  useUserStore,
} from '@repo/front-end-state';
import {
  countPlanContents,
  createEmptyLayout,
  describeFailedSave,
  findLatestDesign,
  floorPlanLandingTab,
  isLive,
  layoutHasGeometry,
  planOpeningFloorIndex,
  readPlanView,
  writePlanView,
  type DraftEntityChanges,
  type FloorPlanStart,
  type PlanChecks,
} from '@repo/front-end-utils';
import type { FloorPlannerLayout } from '@repo/frontend-types';

import Config from '../../common/Config';
import { ORG_BID, ORG_NAME, PLAN_NAME } from './constants';
import { useLiveTwinPlan } from './useLiveTwinPlan';
import { restoreSessionRecordsFrom } from './sessionRecords';
import StartChooser, { type TwinCardState } from './StartChooser';
import type { PlanSaveOutcome } from './OnboardingPage';
import FloorPlanIdentify, {
  PLAN_TOAST_REGION,
  type FloorPlanIdentifyProps,
} from './FloorPlanIdentify';
import { identifyEntityMarks, type IdentifyEntityMarks } from './identifyEntityMarks';
import { planEntityChanges, type PlanEntityChange } from './planEntityChanges';
import FloorPlanMachineCard from './FloorPlanMachineCard';
import type { LiveTwinConfiguration } from './useLiveTwinConfiguration';
import { useFloorPlanCameraPanel } from './useFloorPlanCameraPanel';
import { PlanCard, usePlanCardLayer } from './planCard';
import {
  GLASS_EDGE,
  GLASS_EDGE_PX,
  GLASS_GAP,
  GLASS_START,
  LIST_PANEL_WIDTH,
  PANEL_TOP,
  PANEL_TOP_PX,
  besideCardRight,
} from './glassLayout';
import { usePlanCoverInsets } from './planCoverInsets';

/**
 * The marks this host gives the planner: the search and identify lighting, the search's pick, and the
 * draft's lasting marks.
 */
type PlannerEntityMarks = IdentifyEntityMarks & {
  jumped?: ReadonlySet<string>;
  changes?: ReadonlyMap<string, PlanEntityChange>;
};

interface Props {
  className?: string;
  /**
   * Onboarding run id. Keys this run's floorplan in the store (`twinBID`) — NOT a
   * register scope: the inventory is fetched by `twinId`.
   */
  sessionId: string;
  /**
   * The store scope the register's names may be copied under — `sessionId` once `OnboardingPage`
   * has confirmed the session's plan and the register are the same twin, otherwise `undefined`
   * (`onboarding-polish-plan-labels`). Passed straight to `TwinPlanner`.
   */
  layoutScope?: string;
  /**
   * The twin being set up. Its Asset Register feeds the planner inventory. Required:
   * `OnboardingPage` returns its "No twin selected" page before rendering this
   * (`OnboardingPage.tsx`), so "no twin" is not a state the wizard's planner can be in.
   * It was optional only because the deleted session scope used to cover that case.
   */
  twinId: number;
  /**
   * The twin's dGraph BID, from the wizard's `?twinBid=`.
   *
   * ⚠ NOT the floorplan store's `twinBID`, which this wizard keys on `sessionId` instead (see
   * `loadLayout` calls below) — two different values with the same name. This one identifies the
   * real twin in dGraph and is what *Start from this twin* fetches; the store's identifies the
   * local planner session. Optional because the URL param is.
   */
  twinBid?: string;
  /**
   * The twin's saved plan id, or null when it has none.
   *
   * Non-null means there is already a working plan, so *Start from this twin* is not offered —
   * seeding over authored work would throw that work away (spec FR2.2).
   */
  existingPlanId: number | null;
  /**
   * `persistPlan` (`planSync.ts`) — the twin-plans save the wizard already uses.
   *
   * A fetched plan is persisted through it rather than through anything new (FR4), and its outcome
   * is honoured: a plan that did not reach the server must not enter the planner, and the server's
   * reason — where it gave one — is what the chooser reports (`camera-observer-setup-save-refusal`).
   */
  onPersist: () => Promise<PlanSaveOutcome>;
  /**
   * Where the planner reads stored floor-plan PDF images from — `usePlanSave`'s, because its
   * save is what stores them and seeds this cache (`complex-measures-in-configuration-underlay-
   * storage`).
   */
  underlayImages?: UnderlayImages;
  /**
   * The planner's report that a deletion took camera regions (`onboarding-polish-delete-cascade`).
   * The wizard passes its observer-edit handler, so the deletion stamps the observer draft and a
   * launched twin does not reload the running configuration's regions over it on the next open.
   */
  onObserversRemoved?: () => void;
  /**
   * The planner's Checks model as it changes (`floor-plan-page-checks`, R7): the page shows its
   * *manufacturer identified* score on the step list. `null` while there is no model.
   */
  onPlanChecks?: (checks: PlanChecks | null) => void;
  /**
   * Whether the twin is live or migrating (`floor-plan-page-start`, R1/R3): the landing rule's
   * `isExistingTwinRoute(route)`, computed by the page, so there is one definition of "live or
   * migrating". `null` while the page's twin read has not settled — the live card holds its slot in
   * a loading state and the live model is not read yet. `false` (a new twin) offers only *Draw a new
   * plan*.
   */
  existingTwin: boolean | null;
  /**
   * The page's twin read has settled with NO twin — a failed or timed-out read, no token, or a BID
   * nothing answers to (`getTwinById` cannot tell these apart). The route is then unknowable, not
   * "new": a live gym whose read failed must not be offered only a blank plan, which Launch would
   * read as removing every live entity. The live card says so instead of *Checking…* for ever.
   */
  twinUnreadable?: boolean;
  /**
   * When the configuration now running went live — the production launch's `completedAt` from the
   * page's launch history — or `null` (spec Amendment 2). Shown on the live card.
   */
  liveSince?: string | null;
  /**
   * What the draft changed against production, entity by entity: the page's one change source
   * (`useDraftChanges`). The spaces it added or moved keep their lasting mark on the plan
   * (`onboarding-status-colours-plan-spaces`, R6). Absent, no space is marked.
   */
  draftChanges?: DraftEntityChanges;
  /**
   * The register writes the identify card makes from the Checks tab (`floor-plan-page-identify`):
   * the page's own, the ones the Equipment step's card is given. Absent, the Checks tab's identify
   * rows only frame their machines, as before.
   */
  identify?: Pick<
    FloorPlanIdentifyProps,
    'getToken' | 'currentTwinId' | 'reloadRegister' | 'beginRegisterWrite' | 'settleRegisterWrite'
  >;
  /**
   * The page's read of the twin's running configuration (`useLiveTwinConfiguration`): its streams
   * name and picture a selected camera in the camera panel (`unified-camera-panel`, R1). Absent, the
   * panel says the streams are loading.
   */
  liveConfiguration?: LiveTwinConfiguration;
  /**
   * The plan search (`PlanEntitySearch`), built by the page and shown at the top of the list panel
   * in Edit floor (`onboarding-persistent-scene-floor-plan-glass`, R2.2). Absent, the panel has no
   * Edit floor search.
   */
  planSearch?: React.ReactNode;
  /** The landing visit's draft notices, at the foot of the list panel's header (R2.2). */
  notices?: React.ReactNode;
  /**
   * Told whether the planner is on screen (not the loading line or the start choice), so the page
   * runs the step under the glass only then (R1.3). Told before paint, and `false` on unmount.
   */
  onPlannerShownChange?: (shown: boolean) => void;
}

type Mode = 'choose' | 'planner';

/** The browser's storage, or null where even reaching it throws (a sandboxed frame). */
const viewStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** The live card's reason when the page could not read the twin (see `twinUnreadable`). */
const TWIN_UNREADABLE =
  'This twin could not be read, so its live model cannot be loaded here. Reload the page to try again.';

const NOOP = () => undefined;

/**
 * What the identify card reads from the planner's Checks model, as a string: whether the register is
 * read, the score, each group's key, state, rows, label, what the customer wrote and its stored guess,
 * and each group's placed machines. Equal keys mean the card would render the same, so the newer model
 * is not kept (see `handlePlanChecks`).
 */
export const identifyModelKey = (checks: PlanChecks | null): string => {
  if (!checks) return 'none';
  if (!checks.registerKnown) return 'unread';
  const { brand, base, percent } = checks.score;
  const groups = checks.identify
    .map(
      group =>
        `${group.key}:${group.state}:${group.rowIds.join(',')}:${checks.refidsOfGroup(group.key).join(',')}:${JSON.stringify(
          [
            group.label,
            group.written.category,
            group.written.manufacturer,
            group.proposal?.manufacturer ?? null,
            group.proposal?.reference_id ?? null,
            group.proposal?.reason ?? null,
          ],
        )}`,
    )
    .join(';');
  return `${brand}/${base}/${percent}|${groups}`;
};

/**
 * Where no page binds the identify card's writes (`identify` absent), the planner renders as it did
 * before the card existed: nothing lit, nothing docked, and no toast hook (which needs the page's
 * `ToastProvider`).
 */
const WithoutIdentify: React.FC<Pick<FloorPlanIdentifyProps, 'children'>> = ({ children }) => (
  <>
    {children({
      dock: null,
      openKey: null,
      onIdentifyGroup: NOOP,
      groupLit: null,
      arrived: null,
      status: null,
    })}
  </>
);

/**
 * Whether exactly one live machine is selected on the floor shown, for this session's plan
 * (`floor-plan-page-machine-card`, R1). A boolean selector over both stores, so a drag frame that
 * rewrites the layout re-renders nothing unless the answer changes.
 */
const useMachineSelected = (sessionId: string): boolean => {
  const selectedId = useEditorStore(s =>
    s.selectedEntityIds.length === 1 ? s.selectedEntityIds[0] : null,
  );
  // The card's floor (`plan-fly-to`, R4.3): the held selection's, else the floor shown.
  const held = useEditorStore(s => s.heldSelectionFloor);
  return useFloorplanStore(s => {
    if (!selectedId || s.twinBID !== sessionId || !s.layout) return false;
    const floor = s.layout.floors?.[held ?? s.activeFloorIndex];
    const design = floor ? findLatestDesign(floor.designs) : null;
    return !!design?.items?.some(item => item.refid === selectedId && isLive(item));
  });
};

/**
 * Whether exactly one live space is selected on the floor shown, for this session's plan
 * (`floor-plan-page-space-card`, R1): the space card's twin of `useMachineSelected`, over surfaces.
 */
const useSpaceSelected = (sessionId: string): boolean => {
  const selectedId = useEditorStore(s =>
    s.selectedEntityIds.length === 1 ? s.selectedEntityIds[0] : null,
  );
  const held = useEditorStore(s => s.heldSelectionFloor);
  return useFloorplanStore(s => {
    if (!selectedId || s.twinBID !== sessionId || !s.layout) return false;
    const floor = s.layout.floors?.[held ?? s.activeFloorIndex];
    const design = floor ? findLatestDesign(floor.designs) : null;
    return !!design?.surfaces?.some(surface => surface.refid === selectedId && isLive(surface));
  });
};

/**
 * Whether exactly one live camera is selected on the floor shown, for this session's plan
 * (`unified-camera-panel`, R7): the camera panel then takes the plan card and identify closes, as
 * for a machine.
 */
const useCameraSelected = (sessionId: string): boolean => {
  const selectedId = useEditorStore(s =>
    s.selectedEntityIds.length === 1 ? s.selectedEntityIds[0] : null,
  );
  return useFloorplanStore(s => {
    if (!selectedId || s.twinBID !== sessionId || !s.layout) return false;
    const floor = s.layout.floors?.[s.activeFloorIndex];
    const design = floor ? findLatestDesign(floor.designs) : null;
    return !!design?.cameras?.some(camera => camera.refid === selectedId && isLive(camera));
  });
};

/** *Error, To check, Identify, Selected, OK* — frame 01's legend, in one row in the floating legend bar. */
// One row in the floating legend bar, as frame 01 draws it (R5.2).
const STEP_LEGEND = <PlanLegend framed direction="row" entries={PLAN_ENTITY_LEGEND} />;

/** The plan card's home: below the nav bar, at the 8 px edge, as Camera Feeds' card (R8.1). */
const CARD_HOME_INSET = {
  top: PANEL_TOP_PX,
  right: GLASS_EDGE_PX,
  bottom: GLASS_EDGE_PX,
  left: GLASS_EDGE_PX,
};
/** The room the card layer leaves a tall card: the same top and bottom clearance. */
const CARD_ROOM = { top: PANEL_TOP_PX, bottom: GLASS_EDGE_PX };
/** The list panel's inset: after the step list, 12 px below the bar, 8 px above the bottom (R2.1). */
const LIST_INSET = { top: PANEL_TOP, left: GLASS_START, bottom: GLASS_EDGE };
/** Right of the list panel at its open width, folded or not: nothing moves on fold (R3.1, R5.1). */
const BESIDE_LIST = `calc(${GLASS_START} + ${LIST_PANEL_WIDTH} + ${GLASS_GAP})`;
/** The folded bar's name for each Edit floor tab (R2.4). */
const TAB_NAME: Readonly<Record<SidebarMode, string>> = {
  checks: 'Checks',
  assets: 'Assets',
  library: 'Library',
  hierarchy: 'Hierarchy',
};

const OnboardingPlanner: React.FC<Props> = ({
  className,
  sessionId,
  layoutScope,
  twinId,
  twinBid,
  existingPlanId,
  onPersist,
  underlayImages,
  onObserversRemoved,
  onPlanChecks,
  existingTwin,
  twinUnreadable = false,
  liveSince = null,
  draftChanges,
  identify,
  liveConfiguration,
  planSearch,
  notices,
  onPlannerShownChange,
}) => {
  // Fixed per host — a page binds the card or it does not — so the planner is never remounted.
  const IdentifyHost: React.FC<FloorPlanIdentifyProps> = identify
    ? FloorPlanIdentify
    : WithoutIdentify;
  /*
   * The plan search's matches, lit over the planner's own check marks — combined field by
   * field, so the floor's error marks stay (`floor-plan-page-search`, R4). Read from the editor
   * store, where the page writes it, so a keystroke re-renders this planner and not the page.
   */
  const searchLit = useEditorStore(s => s.searchLitIds);
  /*
   * Exactly one live machine selected on the floor shown (`floor-plan-page-machine-card`, R1–R4): the
   * machine card has the plan card, and the identify card closes. A primitive, so a
   * drag's layout writes re-render nothing here.
   */
  const machineSelected = useMachineSelected(sessionId);
  /*
   * ... or one live space: the space card has the plan card (`floor-plan-page-space-card`, R1–R4;
   * `unified-camera-panel`, R7). The planner draws it and portals it into the cards layer.
   */
  const spaceSelected = useSpaceSelected(sessionId);
  const cameraSelected = useCameraSelected(sessionId);
  /*
   * ... and the identify card's open group, told apart from them, and the machines it has just
   * identified (`floor-plan-page-identify`, R12–R13; `onboarding-status-colours-plan-machines`, R5).
   * The marks come from the card's host inside the render below, so they are memoised on their
   * inputs by hand: a fresh object every render would re-render the whole planner.
   */
  // The spaces and machines the draft added or moved, a new map only when the change source changes.
  const entityChanges = useMemo(
    () => (draftChanges ? planEntityChanges(draftChanges) : null),
    [draftChanges],
  );
  /*
   * The search's last pick (`onboarding-status-colours-plan-machines`, R6): a new one-id set per pick,
   * so the planner glows it again on a second pick. Only a pick made while this planner is mounted:
   * the store keeps the last one after the step closes, and the planner glows whatever it is given
   * when it mounts, so a returning operator would otherwise see an old pick glow again.
   */
  const jumped = useEditorStore(s => s.jumped);
  const jumpedAtMount = useRef(jumped);
  const jumpedSet = useMemo(
    () => (jumped && jumped !== jumpedAtMount.current ? new Set([jumped.id]) : null),
    [jumped],
  );
  const marksMemo = useRef<{
    inputs: readonly unknown[];
    marks: PlannerEntityMarks | undefined;
  } | null>(null);
  const entityMarksFor = (
    groupLit: readonly string[] | null,
    arrived: ReadonlySet<string> | null,
  ): PlannerEntityMarks | undefined => {
    const inputs = [searchLit, groupLit, arrived, entityChanges, jumpedSet];
    const memo = marksMemo.current;
    if (memo && memo.inputs.every((input, i) => input === inputs[i])) return memo.marks;
    const lit = identifyEntityMarks({ searchLit, groupLit, arrived });
    // An empty map or no pick adds nothing, so the marks stay `undefined` exactly as without them.
    const extra = {
      ...(jumpedSet ? { jumped: jumpedSet } : {}),
      ...(entityChanges && entityChanges.size > 0 ? { changes: entityChanges } : {}),
    };
    const marks = Object.keys(extra).length > 0 ? { ...lit, ...extra } : lit;
    marksMemo.current = { inputs, marks };
    return marks;
  };
  /*
   * The planner's Checks model, kept for the identify card as well as handed up to the page. The
   * model is rebuilt on every deferred frame of a drag; keeping each one would re-render this host
   * and the whole planner per frame, which `PlanChecksReporter` exists to avoid. So a new model is
   * kept only when something the card reads has changed (`identifyModelKey`), and only where the
   * page binds the card at all.
   */
  const [planChecks, setPlanChecks] = useState<PlanChecks | null>(null);
  const bindsIdentify = !!identify;
  /*
   * The folded bar's Checks summary (R2.4): two numbers, kept only when one changes, so a drag's
   * per-frame models re-render nothing here.
   */
  const [checksSummary, setChecksSummary] = useState({ toDo: 0, errors: 0 });
  const handlePlanChecks = useCallback(
    (checks: PlanChecks | null) => {
      const toDo = checksToDo(checks);
      const errors = checks?.known ? checks.counts.twin.error : 0;
      setChecksSummary(prev =>
        prev.toDo === toDo && prev.errors === errors ? prev : { toDo, errors },
      );
      if (bindsIdentify) {
        setPlanChecks(prev =>
          identifyModelKey(prev) === identifyModelKey(checks) ? prev : checks,
        );
      }
      onPlanChecks?.(checks);
    },
    [onPlanChecks, bindsIdentify],
  );
  const { getAccessTokenSilently, user } = useAuth0();
  // UI context only — an INPUT to the org check, never its answer. `resolveActivationOrg` derives
  // the twin's namespace from `twin.organisation_id` and refuses on a mismatch, because a TL_ADMIN
  // sees every twin in every org.
  const { authMetadata } = useUserStore();
  const selectedOrgBid = authMetadata?.orgBID;

  const loadLayout = useFloorplanStore(s => s.loadLayout);
  const pushSnapshot = useEditorStore(s => s.pushSnapshot);
  const clearHistory = useEditorStore(s => s.clearHistory);
  const clearModified = useEditorStore(s => s.clearModified);

  const [accessToken, setAccessToken] = useState<string>();
  const [tokenRefused, setTokenRefused] = useState(false);

  /*
   * The plan card (`unified-camera-panel`, R1, R7): one card floating over the plan, top-right, that
   * holds whichever card is open, moved by its grip as the Observers cards are and kept where this
   * person left it.
   */
  const planPaneRef = useRef<HTMLDivElement | null>(null);
  const camera = useFloorPlanCameraPanel({
    sessionId,
    twinId,
    accessToken,
    tokenRefused,
    liveConfiguration,
    planPaneRef,
  });
  const planCards = usePlanCardLayer(user?.sub, camera.announce, CARD_ROOM);

  /*
   * The glass layout (`onboarding-persistent-scene-floor-plan-glass`). The lists are a list panel
   * over the plan, open on every visit and never stored (R2.1); folding is said through the step's
   * existing polite region.
   */
  const [listOpen, setListOpen] = useState(true);
  const announce = camera.announce;
  const onListOpenChange = useCallback(
    (open: boolean) => {
      setListOpen(open);
      announce(open ? 'Floor plan lists opened' : 'Floor plan lists folded');
    },
    [announce],
  );
  const aligning = useEditorStore(s => s.planMode === 'align');
  // Setting the plan scale arms a two-point pick with no dialog over it, so `/` must not pull
  // focus into a search box there (spec, Inputs: the / key). Its dialogs are modal, and block it.
  const calibrating = useEditorStore(s => s.toolMode === 'calibrate_scale');
  const activeFloorName = useFloorplanStore(s =>
    s.twinBID === sessionId ? s.layout?.floors?.[s.activeFloorIndex]?.name : undefined,
  );

  /*
   * The plan card at its home pushes the rail and the floors left of it (R8.2, R3.1); a card moved
   * away leaves both at the edge. The card reports from inside its own ResizeObserver, so both land
   * on the next frame, as on Camera Feeds.
   */
  const [cardAtHome, setCardAtHome] = useState(true);
  /*
   * `plan-fly-to`, R3: what covers the plan — the bar, the step list, the list panel and the plan
   * card while it is open at home. `cardOpen` is known where the card is built, below.
   */
  const cardCoversRef = useRef(false);
  usePlanCoverInsets(planPaneRef, () => cardCoversRef.current);
  const [cardWidth, setCardWidth] = useState(0);
  const cardFrames = useRef(new Map<'size' | 'home', number>());
  const nextFrame = useCallback((key: 'size' | 'home', apply: () => void) => {
    const pending = cardFrames.current.get(key);
    if (pending !== undefined) window.cancelAnimationFrame(pending);
    cardFrames.current.set(
      key,
      window.requestAnimationFrame(() => {
        cardFrames.current.delete(key);
        apply();
      }),
    );
  }, []);
  useEffect(() => {
    const frames = cardFrames.current;
    return () => frames.forEach(id => window.cancelAnimationFrame(id));
  }, []);
  const onCardSize = useCallback(
    (size: { w: number; h: number }) => nextFrame('size', () => setCardWidth(size.w)),
    [nextFrame],
  );
  const onCardAtHomeChange = useCallback(
    (atHome: boolean) => nextFrame('home', () => setCardAtHome(atHome)),
    [nextFrame],
  );

  // *Start from this twin*: the store-load-and-save half, after the fetch has already succeeded.
  // Separate from the hook's `loading` because they fail differently — the hook's failures disable
  // the option, this one keeps the user on the chooser with a message.
  const [adopting, setAdopting] = useState(false);
  const [adoptError, setAdoptError] = useState<string | null>(null);
  // Skip the chooser if a plan for this session is already in the stores (e.g.
  // the user navigated to step 3 and back).
  const [mode, setMode] = useState<Mode>(() => {
    const fp = useFloorplanStore.getState();
    return fp.layout && fp.twinBID === sessionId ? 'planner' : 'choose';
  });

  /*
   * The toasts sit 12 px above the legend bar (R6.2): its height, measured where it is drawn, lands
   * on the next frame. The bar mounts with the planner, so it is looked for once the planner shows.
   */
  const [legendHeight, setLegendHeight] = useState(0);
  useEffect(() => {
    const pane = planPaneRef.current;
    if (mode !== 'planner' || !pane || typeof ResizeObserver === 'undefined') return undefined;
    let frame = 0;
    let sized: ResizeObserver | null = null;
    const attach = (): boolean => {
      const legend = pane.querySelector<HTMLElement>('[data-floating-legend]');
      if (!legend) return false;
      sized = new ResizeObserver(() => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() =>
          setLegendHeight(Math.round(legend.getBoundingClientRect().height)),
        );
      });
      sized.observe(legend);
      return true;
    };
    let waiting: MutationObserver | null = null;
    if (!attach()) {
      waiting = new MutationObserver(() => {
        if (attach()) waiting?.disconnect();
      });
      waiting.observe(pane, { childList: true, subtree: true });
    }
    return () => {
      waiting?.disconnect();
      sized?.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [mode]);
  const toastBottomInset = `calc(${GLASS_EDGE} + ${legendHeight}px + ${GLASS_GAP})`;

  /*
   * Where the step opens, and where it was left (`floor-plan-page-start`, R8–R9).
   *
   * The sidebar takes its tab once, on mount (and again on its remount after *Build floors*), so the
   * tab is decided BEFORE the planner renders: in `enterPlanner` for a chooser card, and in this
   * initialiser when the step opens straight onto a plan. `tab` then follows every change the
   * sidebar reports, and is what a remount opens on. Remembered per twin in this browser — a
   * per-viewer convenience, so a blocked or throwing storage falls back to the defaults, with one
   * warning per mount.
   */
  const viewWarnedRef = useRef(false);
  const rememberedFloorRef = useRef<number | undefined>(undefined);
  const warnViewUnavailable = useCallback(() => {
    if (viewWarnedRef.current) return;
    viewWarnedRef.current = true;
    console.warn('[floor-plan-start] remembered view unavailable', { twinBid });
  }, [twinBid]);
  const landingTab = (start: FloorPlanStart): SidebarMode => {
    if (start === 'resume' && twinBid) {
      const remembered = readPlanView(viewStorage(), twinBid);
      if (!remembered.ok) warnViewUnavailable();
      else {
        rememberedFloorRef.current = remembered.view?.floorId;
        if (remembered.view?.tab) return remembered.view.tab;
      }
    }
    const catalog = useEquipmentCatalogStore.getState();
    return floorPlanLandingTab({
      start,
      // Only a twin KNOWN to be new resumes on the draw rule. An unknown route (an unreadable twin)
      // resumes on Checks, which lists what is wrong and never claims anything about the register.
      existingTwin: existingTwin !== false,
      register: {
        state: registerReadState(catalog, { twinId }),
        rows: catalog.registerRows.length,
      },
    });
  };
  const [tab, setTab] = useState<SidebarMode | null>(() =>
    mode === 'planner' ? landingTab('resume') : null,
  );
  const rememberView = useCallback(
    (view: Parameters<typeof writePlanView>[2]) => {
      if (twinBid && !writePlanView(viewStorage(), twinBid, view)) warnViewUnavailable();
    },
    [twinBid, warnViewUnavailable],
  );
  // Before paint, so a resumed plan opens under the glass in its first frame, and a fetched plan
  // being saved keeps the start choice's own layout until the planner takes over.
  useLayoutEffect(() => {
    onPlannerShownChange?.(mode === 'planner');
  }, [mode, onPlannerShownChange]);
  useLayoutEffect(() => () => onPlannerShownChange?.(false), [onPlannerShownChange]);
  /** Leave the chooser for the planner, on the tab the start chose (R8). */
  const enterPlanner = (start: Exclude<FloorPlanStart, 'resume'>) => {
    const landing = landingTab(start);
    setTab(landing);
    rememberView({ tab: landing });
    setMode('planner');
  };
  const handleModeChange = useCallback(
    (next: SidebarMode) => {
      setTab(next);
      rememberView({ tab: next });
    },
    [rememberView],
  );

  /*
   * The floor half of R9. Restored once, when the step opens straight onto a plan with a floor
   * remembered and that floor still on it (by id — an index would reopen the wrong floor after a
   * reorder); then every floor change is remembered. Only after the restore has run, so the
   * store's default floor never overwrites the one being restored.
   */
  const floorRestoredRef = useRef(false);
  const activeFloorId = useFloorplanStore(s =>
    s.twinBID === sessionId ? s.layout?.floors?.[s.activeFloorIndex]?.id : undefined,
  );
  useEffect(() => {
    if (mode !== 'planner' || floorRestoredRef.current) return;
    floorRestoredRef.current = true;
    const wanted = rememberedFloorRef.current;
    if (wanted === undefined) return;
    const fp = useFloorplanStore.getState();
    const index = planOpeningFloorIndex(fp.layout?.floors, wanted, fp.activeFloorIndex);
    if (index !== fp.activeFloorIndex) fp.setActiveFloor(index);
  }, [mode]);
  useEffect(() => {
    if (mode !== 'planner' || !floorRestoredRef.current || activeFloorId === undefined) return;
    rememberView({ floorId: activeFloorId });
  }, [mode, activeFloorId, rememberView]);

  // When the plan is cleared (store reset → no session layout), drop back to the
  // chooser, so the planner never sits over a null layout.
  const hasSessionLayout = useFloorplanStore(s => s.layout != null && s.twinBID === sessionId);
  useEffect(() => {
    if (!hasSessionLayout) {
      setMode('choose');
      setTab(null);
    }
  }, [hasSessionLayout]);

  // The place-camera tool is armable on this step as of `camera-is-a-tool`, and `toolMode` is
  // global editor state with no reset on navigation — so an armed tool used to be able to survive
  // Floor Plan -> Equipment -> Floor Plan and turn the operator's next click (meant to select an
  // asset) into a stray camera, with the stage un-pannable until they found the select tool.
  // `OnboardingCameras` already clears the mode on mount for the same reason; this is its mirror.
  const setToolMode = useEditorStore(s => s.setToolMode);
  useEffect(() => {
    setToolMode('select');
  }, [setToolMode]);

  // Resolve the access token once.
  useEffect(() => {
    let cancelled = false;
    getAccessTokenSilently().then(
      token => {
        if (!cancelled) setAccessToken(token);
      },
      (error: unknown) => {
        // Said, never swallowed: the camera panel's pictures show they cannot be read, not loading.
        if (cancelled) return;
        console.warn('[floor-plan] access token unavailable', (error as Error)?.message);
        setTokenRefused(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  /*
   * The register, for *Draw a new plan*'s fact and its landing tab (R5, R8). The sidebar is not
   * mounted on the chooser, so the chooser asks the store with the sidebar's own arguments
   * (`EquipmentSidebar`, `retryRead`): the store joins a read already in flight rather than issuing
   * a second, and keeps its own paging and bounds.
   */
  const loadRegisterAssets = useEquipmentCatalogStore(s => s.loadRegisterAssets);
  // The count, not the rows: a primitive, so a register write does not re-render the planner below.
  const registerRowCount = useEquipmentCatalogStore(s => s.registerRows.length);
  const registerState = useEquipmentCatalogStore(s => registerReadState(s, { twinId }));
  useEffect(() => {
    if (mode !== 'choose') return;
    void loadRegisterAssets(Config.middlewareApi, getAccessTokenSilently, { twinId });
  }, [mode, loadRegisterAssets, getAccessTokenSilently, twinId]);

  /** Seed the stores with a blank layout. */
  const startBlank = () => {
    const layout = createEmptyLayout(PLAN_NAME);
    loadLayout(layout, sessionId, ORG_BID, PLAN_NAME, ORG_NAME, { markDirty: true });
    clearHistory();
    clearModified();
    restoreSessionRecordsFrom(layout);
    pushSnapshot(layout, 'baseline');
  };

  // ---- *Start from this twin* (spec `plan-from-existing-twin`; `floor-plan-page-start`, R3) ----
  //
  // Only ask for the live model where seeding could not destroy anything (FR2.2), and only for a
  // twin the route rule calls live or migrating — a new twin has no live model, and its chooser
  // offers *Draw a new plan* alone. The fetch is SKIPPED rather than merely disabled when any of
  // these fails — a ten-deep entity tree for a twin of several hundred entities is not free.
  //
  // In practice the chooser is already unreachable once a plan exists (`planSync.ts`'s resume
  // effect loads it, which puts `mode` at 'planner' below), so this is the belt to that braces —
  // it also covers a plan row whose `layout` column was empty, which resume skips.
  const sessionLayout = useFloorplanStore(s => (s.twinBID === sessionId ? s.layout : null));
  const canAdoptLiveModel =
    mode === 'choose' &&
    existingTwin === true &&
    existingPlanId == null &&
    // `|| adopting`: committing writes the fetched layout into the store BEFORE saving it (the save
    // path reads the store, not a closure), which flips `layoutHasGeometry` true mid-commit. Without
    // this term the option would unmount the instant it was pressed — taking its own progress and,
    // on a failed save, the retry with it.
    (adopting || !layoutHasGeometry(sessionLayout));

  const liveTwinPlan = useLiveTwinPlan({
    twinBid,
    accessToken,
    selectedOrgBid,
    enabled: canAdoptLiveModel,
  });

  const liveLayout = liveTwinPlan.status === 'available' ? liveTwinPlan.layout : null;
  const liveCounts = useMemo(() => countPlanContents(liveLayout), [liveLayout]);
  /*
   * The live card (R3–R4). Not offered where it cannot be honest: a new twin, no twin BID, or a
   * plan already here (`canAdoptLiveModel`). While the route is unknown it holds its slot as
   * *Checking…* rather than appearing a moment later.
   */
  const awaitingRoute =
    existingTwin === null &&
    !!twinBid &&
    existingPlanId == null &&
    !layoutHasGeometry(sessionLayout);
  const twinCard: TwinCardState =
    !twinBid || existingTwin === false
      ? { kind: 'hidden' }
      : awaitingRoute
        ? twinUnreadable
          ? { kind: 'unavailable', reason: TWIN_UNREADABLE }
          : { kind: 'loading', line: 'Checking this twin’s live model…' }
        : !canAdoptLiveModel
          ? { kind: 'hidden' }
          : liveTwinPlan.status === 'loading'
            ? { kind: 'loading', line: 'Checking this twin’s live model…' }
            : liveTwinPlan.status === 'unavailable'
              ? { kind: 'unavailable', reason: liveTwinPlan.reason }
              : {
                  kind: 'available',
                  counts: liveCounts,
                  skipped: liveTwinPlan.skipped,
                  liveSince,
                  adopting,
                };

  /**
   * Start the plan from the twin's live model.
   *
   * Everything that can fail already happened in `useLiveTwinPlan` — this runs only for an
   * `available` state, so the click itself cannot throw (FR1: "It must not enter the planner on
   * failure"). What is left is the same store sequence the other two start options use, plus the
   * save.
   */
  const startFromLiveTwin = async (
    fetched: FloorPlannerLayout,
    skipped: { count: number; summary: string } | null,
  ) => {
    /*
     * What the conversion skipped rides the plan from its FIRST save (`floor-plan-page-start`, R7):
     * an entity missing from the plan reads as a removal at Launch, so the note must survive the
     * reload and reach whoever opens the plan next. It heads Checks until a launch clears it.
     */
    const layout =
      skipped && skipped.count > 0 ? { ...fetched, adoptionSkipped: skipped } : fetched;
    setAdopting(true);
    try {
      // `sessionId` and `ORG_BID` are correct HERE and nowhere else in this feature: they are the
      // store's session bookkeeping, they never reach dGraph, and using anything else would break
      // the `twinBID === sessionId` checks this component and `OnboardingPage` both key on.
      //
      // `markDirty: false` — the deliberate difference from `startBlank`. A
      // fetched plan is a faithful copy of what is already live, so nothing is unsaved until the
      // user edits it. It reads like an oversight; it is not.
      loadLayout(layout, sessionId, ORG_BID, PLAN_NAME, ORG_NAME, { markDirty: false });
      clearHistory();
      clearModified();
      restoreSessionRecordsFrom(layout);
      pushSnapshot(layout, 'baseline');

      // AFTER `loadLayout`, because `persistPlan` reads the layout from the store rather than from
      // a closure. Its result is honoured: entering the planner with a fetched plan the server does
      // not have is precisely how the persisted plan and the live model silently diverge.
      const outcome = await onPersist();
      if (!outcome.saved) {
        // ROLL THE STORE BACK. Left in place, an unsaved fetched layout would tick the Floor Plan
        // step complete and count toward Go Live while the server has nothing — and the next Save
        // or Go Live would treat a copy of the live model as the user's authored plan. Resetting is
        // safe precisely because the option is only offered when there was nothing to lose, so
        // there is nothing here to restore.
        useFloorplanStore.getState().reset();
        clearHistory();
        clearModified();
        restoreSessionRecordsFrom(null);
        // The server's reason where it gave one — a refusal is refused identically for ever, so
        // the retry advice is kept only for a failure the server did not explain.
        setAdoptError(
          describeFailedSave(
            'Could not save the fetched plan, so nothing was changed.',
            outcome.reason,
            'Please try again.',
          ),
        );
        return;
      }
      enterPlanner('twin');
    } finally {
      setAdopting(false);
    }
  };

  // The equipment sidebar loads the twin's Asset Register itself (see
  // `EquipmentCatalogStore.loadRegisterAssets`), so the bespoke fetch-map-inject
  // effect that used to live here is gone. It existed only because the sidebar's
  // own two remote sources called middleware routes that have never existed, so it
  // had to re-derive inventory from the register on every mount and push it in
  // optimistically — with the failing persistence `PUT` swallowed each time. Both
  // planners now share one path.
  return (
    <div className={className}>
      {mode === 'choose' ? (
        <StartChooser
          // One glass card over the scrim (`onboarding-persistent-scene-floor-plan-glass`, R7).
          look="glass"
          notices={notices}
          existingTwin={existingTwin}
          twin={twinCard}
          register={{ state: registerState, rows: registerRowCount }}
          error={adoptError}
          onStartFromTwin={() => {
            // Belt to the card's `disabled` braces: a press in any other state does nothing rather
            // than reading `layout` off a state that has none.
            if (liveTwinPlan.status !== 'available' || adopting) return;
            setAdoptError(null);
            // `skipped` counts exactly the entities `warning` describes (`skippedCount`), so a note is
            // saved only with the converter's own sentence — never one invented here.
            const { layout, skipped, warning } = liveTwinPlan;
            void startFromLiveTwin(
              layout,
              skipped > 0 && warning ? { count: skipped, summary: warning } : null,
            );
          }}
          onDrawNewPlan={() => {
            // Not while an adoption is saving: its rollback (or its success) would land on the plan
            // this press loads. The card's button is disabled too; this is the belt.
            if (adopting) return;
            startBlank();
            enterPlanner('draw');
          }}
        />
      ) : (
        <IdentifyHost
          twinId={twinId}
          planChecks={planChecks}
          // The card belongs to the Checks tab (R4); leaving the step unmounts it.
          active={tab === 'checks'}
          // Selecting a machine (or a space or a camera) CLOSES it: that card takes the plan card
          // (`floor-plan-page-machine-card`, R4). Closed, not disabled — a Checks identify row clears the
          // selection and opens its group in one press, and the open must not be refused.
          closedBy={machineSelected || spaceSelected || cameraSelected}
          baseUrl={Config.middlewareApi}
          getToken={identify?.getToken ?? getAccessTokenSilently}
          currentTwinId={identify?.currentTwinId ?? (() => null)}
          reloadRegister={identify?.reloadRegister ?? NOOP}
          beginRegisterWrite={identify?.beginRegisterWrite ?? (() => null)}
          settleRegisterWrite={identify?.settleRegisterWrite ?? NOOP}
        >
          {({ dock, openKey, onIdentifyGroup, groupLit, arrived, status }) => {
            /*
             * The machine card's host stays mounted for the whole step and draws nothing between
             * selections, so a write refused after the card closed is still shown when the machine is
             * opened again, and an identify toast's Undo outlives the card that raised it.
             */
            const machineCard = identify ? (
              <FloorPlanMachineCard
                twinId={twinId}
                twinBid={twinBid}
                baseUrl={Config.middlewareApi}
                getToken={identify.getToken ?? getAccessTokenSilently}
                currentTwinId={identify.currentTwinId}
                reloadRegister={identify.reloadRegister}
                beginRegisterWrite={identify.beginRegisterWrite}
                settleRegisterWrite={identify.settleRegisterWrite}
                toastRegion={PLAN_TOAST_REGION}
              />
            ) : null;
            /*
             * One plan card, floating over the canvas (`unified-camera-panel`, R7): a selected
             * camera's panel, else a selected machine's card, else (nothing selected) the identify
             * card. The space card has the same card through `wrapSpaceCard`, because the planner
             * draws it; it shows only while a space is selected, when this one holds nothing. Both read
             * the one stored position, so a swap does not move the card.
             */
            const showDock = !machineSelected && !spaceSelected && !cameraSelected;
            const cardName = camera.panel
              ? 'camera panel'
              : machineSelected
                ? 'machine card'
                : dock && showDock
                  ? 'identify panel'
                  : 'plan card';
            /*
             * The machine card's 650 px minimum (`floor-plan-item-cards`, R2) only while the plan
             * card holds it: never the camera panel or the identify card, which keep their heights.
             */
            const machineCardShown = machineCard !== null && cardName === 'machine card';
            /*
             * Whether a card is open at its home corner, which the rail and the floors clear
             * (R8.2, R3.1): any card the plan card holds, or the space card.
             */
            const cardOpen = cardName !== 'plan card' || spaceSelected;
            cardCoversRef.current = cardOpen && cardAtHome;
            const besideCard = (gaps: number) =>
              besideCardRight({ open: cardOpen, atHome: cardAtHome, width: cardWidth }, gaps);
            const wrapSpaceCard = (spaceCard: React.ReactNode) => (
              <PlanCard
                frame={planCards.frame}
                name="space card"
                homeInset={CARD_HOME_INSET}
                onAtHomeChange={onCardAtHomeChange}
                onSize={onCardSize}
                minHeight
              >
                {spaceCard}
              </PlanCard>
            );
            // The plan card's layer, over the canvas and clear of the rail (`canvasOverlay`).
            const cardsOverlay = planCards.render(
              <PlanCard
                frame={planCards.frame}
                name={cardName}
                homeInset={CARD_HOME_INSET}
                onAtHomeChange={onCardAtHomeChange}
                onSize={onCardSize}
                minHeight={machineCardShown}
              >
                {machineCard}
                {camera.panel}
                {showDock ? dock : null}
              </PlanCard>,
            );
            return (
              <>
                <div className="plan-pane" ref={planPaneRef}>
                  <TwinPlanner
                    showToolbar={false}
                    showFullscreenButton={false}
                    // The machine card edits a selected machine, not the floating panel; with it, the
                    // hover card, the arrow nudge, Shift's 15° turn and the live-delete confirmation
                    // (`floor-plan-page-machine-card`). Only where the page binds the card's writes.
                    itemEditing={identify ? 'host' : 'panel'}
                    planToastRegion={PLAN_TOAST_REGION}
                    // The space card's dock (`floor-plan-page-space-card`): only where the page binds the
                    // cards, so it never appears without the toast region above. Unbound, it is omitted
                    // and a space keeps the floating panel.
                    spaceCardSlot={identify ? planCards.layerElement : undefined}
                    wrapSpaceCard={wrapSpaceCard}
                    // The full toolset: select / wall / space / **camera** (and the rail's *Add reference*,
                    // zoom, Undo / Redo, rotate and overlays — `CanvasControls`). A camera is
                    // infrastructure, not inventory, so it is placed with the canvas tool (or `C`) rather
                    // than dragged out of the equipment Library — which places equipment and only
                    // equipment. This step used to pass `'plan'`, which was `full` minus the place-camera
                    // tool; `toolset-cleanup` then deleted that value entirely. `PlannerToolset` is now
                    // `'full' | 'select' | 'cameras'`, and `CanvasControls.test.tsx` pins what each renders.
                    toolset="full"
                    underlayImages={underlayImages}
                    onObserversRemoved={onObserversRemoved}
                    onPlanChecks={handlePlanChecks}
                    // The *Edit floor* / *Align floors* switch at the right of the floor strip
                    // (`complex-measures-in-configuration-plan-alignment-mode`). This step is its only host:
                    // admin's plan editor does not offer it (Rob, 2026-10-02).
                    showPlanModeSwitch
                    // The Floor Plan design's frame (`floor-plan-page-stage`): each state and each
                    // floor's open errors from the planner's Checks model, and the legend that names
                    // the marks, bottom-left on the plan.
                    floorDialogs="page"
                    planChecks
                    entityMarks={entityMarksFor(groupLit, arrived)}
                    canvasLegend={STEP_LEGEND}
                    canvasOverlay={cardsOverlay}
                    // The glass furniture (`onboarding-persistent-scene-floor-plan-glass`, R2–R5, R8):
                    // the lists in a list panel over the plan, the floors floating and editable at the
                    // top of the free area, the legend bar (with Show map) at its bottom left, and the
                    // rail beside a card at home.
                    sidePanel={{
                      open: listOpen,
                      onOpenChange: onListOpenChange,
                      inset: LIST_INSET,
                      width: LIST_PANEL_WIDTH,
                      searchSlot: planSearch,
                      searchLabel: 'Search the plan and register',
                      notices,
                      foldedName: aligning ? 'Floors' : TAB_NAME[tab ?? 'checks'],
                      foldedSummary: aligning
                        ? activeFloorName
                          ? `${activeFloorName} selected`
                          : undefined
                        : (tab ?? 'checks') === 'checks'
                          ? `${checksSummary.toDo} to do`
                          : undefined,
                      foldedTrail:
                        !aligning && (tab ?? 'checks') === 'checks' && checksSummary.errors > 0 ? (
                          <Tag surface="on-glass" tone="error" size="dense">
                            {checksSummary.errors} {checksSummary.errors === 1 ? 'error' : 'errors'}
                          </Tag>
                        ) : undefined,
                      shortcutEnabled: !calibrating,
                      regionLabel: 'Floor plan lists',
                      foldLabel: 'Fold the floor plan lists',
                      openLabel: 'Open the floor plan lists',
                    }}
                    floorSelector="floating"
                    floorSelectorSlot={{ left: BESIDE_LIST, right: besideCard(2), top: PANEL_TOP }}
                    legendLook="floating"
                    legendInset={{ left: BESIDE_LIST, bottom: GLASS_EDGE, right: besideCard(1) }}
                    showMapSwitch
                    controlsInset={{ top: PANEL_TOP, right: besideCard(1) }}
                    // The twin's real dGraph BID (`?twinBid=`), NOT the store's `twinBID` — this wizard
                    // keys the store on `sessionId` (see the prop's own doc above). The properties panel's
                    // BID field needs the real one to accept the short form an operator types, since ruling
                    // R18 made the stored identity the full BID.
                    twinBid={twinBid}
                    // The store's scope, vouched for by `OnboardingPage` only while the plan and the
                    // register are one twin, so the register's names reach matched machines on the plan
                    // (`onboarding-polish-plan-labels`).
                    layoutScope={layoutScope}
                    equipmentPanel={{
                      apiBaseUrl: Config.middlewareApi,
                      // Auth0's own getter, not the `accessToken` resolved once above: the panel hands it
                      // to the register store as the write capability behind every drop, and a value
                      // captured on mount is long expired on a real onboarding session
                      // (`wizard-register-on-store`, ledger row 46). The SDK caches, so this costs nothing
                      // until a refresh is genuinely due.
                      getAccessToken: getAccessTokenSilently,
                      // The register's only scope: this covers assets uploaded by ANY
                      // onboarding run against the twin, not just the current session.
                      twinId,
                      // The plan search covers the register, so the inventory has no box of its own
                      // on this step (`floor-plan-page-search`, R6). Admin keeps its box.
                      showSearch: false,
                      // The Checks tab, first of four, and the hover it shares with the plan
                      // (`floor-plan-page-checks`). Admin does not ask, so keeps its three tabs.
                      showChecks: true,
                      // The tab the start chose, or the one it was left on (`floor-plan-page-start`, R8–R9).
                      initialMode: tab ?? undefined,
                      onModeChange: handleModeChange,
                      // A Checks identify row opens the identify card on its group (R1–R2).
                      onIdentifyGroup: identify ? onIdentifyGroup : undefined,
                      identifyOpenKey: openKey,
                      // Frame 09's Assets tab — placement chips, manufacturer-grouped rows with a grip —
                      // and the drag card and floor warning (`floor-plan-page-assets-library`). Admin
                      // keeps its cards.
                      assetsLook: 'rows',
                    }}
                  />
                  {/* Every toast the step raises: the identify card's, the machine card's, the
                    delete's, a drawn space's (`floor-plan-page-identify` R15;
                    `floor-plan-page-machine-card` R28; `floor-plan-page-assets-library` R18) and the
                    floor delete's (the planner hands it the floor toasts while the page edits its own
                    cards, as it does every other plan toast); centred on the window, 12 px above the
                    legend bar (`onboarding-persistent-scene-floor-plan-glass`, R6). */}
                  <ToastRegion
                    name={PLAN_TOAST_REGION}
                    placement="bottom-centre"
                    bottomInset={toastBottomInset}
                  />
                  {camera.announcer}
                </div>
                {status}
              </>
            );
          }}
        </IdentifyHost>
      )}
    </div>
  );
};

export default styled(OnboardingPlanner)`
  height: 100%;
  width: 100%;
  position: relative;

  /* The whole step under the glass (onboarding-persistent-scene-floor-plan-glass, R1): every list,
     card and toast floats over it. Positioned, so they place against it. */
  .plan-pane {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;
